import type { SendChannel, SendRequest } from '@/services/notificationService'
import { segmentsFor } from '@/utils/segments'
import { channelKind, sortForPanels } from './channelKinds'

/** What the operator typed in one channel's panel. */
export type ChannelDraft = { body: string; subject: string }

export const EMPTY_DRAFT: ChannelDraft = { body: '', subject: '' }

export type ChannelDrafts = Record<number, ChannelDraft>

type Content = Pick<
  SendRequest,
  'channelIds' | 'body' | 'channelContent' | 'segmentsEstimate'
>

/**
 * The channel part of a send request, from the ticked channels and their
 * drafts. Each ticked channel sends its own text under channelContent. The
 * Gateway still demands one shared body: it takes the first panel's text, and
 * the worker only falls back to it for a channel whose own text is blank.
 */
export const buildContent = (
  channels: SendChannel[],
  tickedIds: number[],
  drafts: ChannelDrafts
): Content => {
  const ticked = sortForPanels(channels).filter(c =>
    tickedIds.includes(c.channelId)
  )
  const draftOf = (c: SendChannel) => drafts[c.channelId] ?? EMPTY_DRAFT
  const channelContent: NonNullable<SendRequest['channelContent']> = {}
  for (const c of ticked) {
    const { body, subject } = draftOf(c)
    channelContent[String(c.channelId)] =
      channelKind(c) === 'email' && subject.trim()
        ? { body, subject }
        : { body }
  }
  const sms = ticked.find(c => channelKind(c) === 'sms')
  return {
    channelIds: ticked.map(c => c.channelId),
    body: ticked[0] ? draftOf(ticked[0]).body : '',
    channelContent,
    ...(sms
      ? {
          segmentsEstimate: (({ encoding, chars, segments }) => ({
            encoding,
            chars,
            segments,
          }))(segmentsFor(draftOf(sms).body)),
        }
      : {}),
  }
}
